import React, { useEffect, useState } from 'react';
import { Button, Loader, Alert, Whisper, Popover } from 'rsuite';

let cachedData = null;
let isDataFetched = false;

const DBActions = ({brands = []}) => {  
  const [isWorking, setIsWorking] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [confirmChoice, setConfirmChoice] = useState(false);
  const [uncheckedJobNumber, setUncheckedJobNumber] = useState(false);
  const [dbCounts, setDbCounts] = useState(() => {
    // Initialize from cached data if available
    return cachedData || {
      total: null,
      lastWeek: null,
      lastMonth: null,
    };
  });
  const [brandAssign, setBrandAssign] = useState({
    jobNumber: '',
    brand: '',
  });
  const [imageCount, setImageCount] = useState({ count: null, countWithoutBrand: null });
  const serverURL = window.location.href.indexOf('localhost') !== -1 ? "http://localhost:8889/.netlify/functions" : "https://gh-imagebank.netlify.app/.netlify/functions";

  const assignBrands = async () => {
    console.log('Assigning brand to images...',JSON.stringify(brandAssign));
    //setIsWorking(true);
    const response = await fetch(`${serverURL}/assignBrand`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(brandAssign)
    });
    const data = await response.json();
    console.log('Response:', data);  
    if (data.status === 'success') {
      Alert.success('Images assigned successfully.', 6000);
    } else {
      Alert.error('Error assigning brand.', 6000);
    }
  }

  const doubleCheck = () => {
    const jobNumber = document.getElementById('jobNumber').value;
    const brand = document.getElementById('brandSelect').value;
    if (!jobNumber || !brand) {
      Alert.error('Please fill in all fields.', 3000);
      return;
    }
    setBrandAssign({ jobNumber, brand });
    setConfirmChoice(true);
    checkJobNumber(jobNumber);
  }

  const checkJobNumber = async (jobNumber) => {
    setUncheckedJobNumber(true);
    // Call the server with GET request to check if the job number exists
    const response = await fetch(`${serverURL}/checkJobNumber?jobNumber=${jobNumber}`);
    const data = await response.json();
    console.log('Job number check response:', data);
    if (data.count !== 0) {
      Alert.info(`Found ${data.count} images with job number ${jobNumber}. ${data.countWithoutBrand} images were brand-less.`, 6000);
    }
    setImageCount({ count: data.count, countWithoutBrand: data.countWithoutBrand });
    setUncheckedJobNumber(false);
  }


  const ImageCountsPopover = (
    <Popover>
      <p>Found {imageCount.count} images in the database. {imageCount.countWithoutBrand} of those images were not assigned any brand.</p>
      { imageCount.countWithoutBrand == 0 && <p className="text-danger">You can only run the tool if images without a brand are found.</p> }
    </Popover>
  );

  const inputChanged = (e) => {
    setConfirmChoice(false);
  }

  const syncImages = async () => {
    setIsWorking(true);
    setStreamText('Starting sync process...\n');
    try {
      const source = new EventSource(`${serverURL}/db-updateLastWeek`);
      source.onmessage = (event) => {
        console.log(event);
        const message = event.data;
        if(message === 'complete') {
          source.close();
          setIsWorking(false);
        } else {
          setStreamText(prevText => prevText + message + '\n');
        }
      };

      source.onerror = (err) => {
        source.close();
        if (source.readyState === EventSource.CLOSED) {
          setStreamText(prev => prev + '\nSync completed.');
          setIsWorking(false);
        }
      };
      
      // Close event source after 10 seconds
      setTimeout(() => {
        if (source.readyState !== EventSource.CLOSED) {
          source.close();
          setStreamText(prev => prev + '\nSync process timed out.');
          setIsWorking(false);
        }
      }, 28000);
      
    } catch (error) {
      console.error('Error syncing images:', error);
    } 
  }

  useEffect(() => {
    // component mounted
    if (isDataFetched || cachedData?.total > 0 || dbCounts.total > 0) {
      setIsWorking(false);
      return;
    }
    const fetchCounts = async () => {
      setIsWorking(true);
      console.log('Fetching counts from server...');
      try {
        const response = await fetch(`${serverURL}/getCounts`);
        const data = await response.json();
        setDbCounts(data);
        isDataFetched = true;
        cachedData = data;
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsWorking(false);
      }
    };

    fetchCounts();

    return () => {
      // component unmounted
    }
  }, [])

  return (
    <div className="text-left">
      <div className="d-flex flex-row justify-content-between card card-body my-2 pb-0 shadow-sm rounded">
        <div className="d-flex flex-column text-center mb-4">
          <p className="mb-0">Total images in DB</p>
          <p className="mb-0 h4">{dbCounts.total !== null ? dbCounts.total.toLocaleString() : <Loader />}</p>
        </div>
        <div className="d-flex flex-column text-center mb-4">
          <p className="mb-0 ">Images added last month</p>
          <p className="mb-0 h4">{dbCounts.lastMonth !== null ? dbCounts.lastMonth.toLocaleString() : <Loader />}</p>
        </div>
        <div className="d-flex flex-column text-center mb-4">
          <p className="mb-0 ">Images added last week</p>
          <p className="mb-0 h4">{dbCounts.lastWeek !== null ? dbCounts.lastWeek.toLocaleString() : <Loader />}</p>
        </div>
      </div>
      <div className="d-md-flex align-items-start justify-content-between">
        <div className="text-center my-4 card card-body mr-md-4" style={{ flexBasis: '45%' }}>
          <h5 className="mb-1">Assign brand to images not displaying in ImageBank</h5>
          <p>If you have uploaded images with a new or unknown job number to Flickr, they won't be assigned to any ImageBank. Use this tool to add them to a specific ImageBank.</p>
          <p><strong>Use with caution!</strong> If you assign a job number to the wrong brand, those images will appear on the wrong ImageBank.</p>
          <form onSubmit={e => {
            e.preventDefault();
            doubleCheck();
          }}>
            <div className="my-2 d-flex text-center">
              <input required placeholder="Job number" className="form-control" id="jobNumber" onChange={inputChanged} />
            </div>
            <div className="mb-2 d-flex ">
              <select required className="form-control" id="brandSelect"  onChange={inputChanged} >
                <option value="">Select brand</option>
                {brands.map((brand, index) => (
                  <option key={index} value={brand.label}>{brand.name}</option>
                ))}
              </select>
            </div>
            <Button 
              type="submit"
              disabled={isWorking}
              appearance="primary"
              block
            >
              Confirm
            </Button>
          </form>
          { confirmChoice && 
            <div className="my-3">
              <p className="font-weight-bold">Are you sure you want to want to do this?</p>
              <p>This action will make all images starting with '<strong>{brandAssign.jobNumber}</strong>' appear in the <strong>{brandAssign.brand}</strong> imagebank.</p>
              <Whisper placement="top" trigger="hover" speaker={ImageCountsPopover} visible={imageCount.countWithoutBrand}>
                <Button   
                  disabled={uncheckedJobNumber || imageCount.countWithoutBrand == 0}
                  appearance="ghost"
                  color="red"
                  block
                  onClick={assignBrands}
                >
                { uncheckedJobNumber ? (
                  <>
                    <span>Checking database</span>
                    <Loader /> 
                  </>
                ) : `Assign brand to ${imageCount.countWithoutBrand} images` }
              </Button>
            </Whisper>
            </div>
          }
        </div>
        <div className="text-center my-4 card card-body" style={{ flexBasis: '45%' }}>
          <h5 className="mb-3">Sync Images from Flickr to ImageBank</h5>
          <p>If you think there's been some issue with the auto-sync between Flickr and ImageBank, you can trigger a manual sync here.</p>
          <Button 
            onClick={syncImages}
            disabled={isWorking}
            appearance="primary"
          >
            Get Images From Last 7 Days
          </Button>
          {streamText && (
            <div className=" my-3">
              <h6>Sync Progress:</h6>
              <div 
                style={{ 
                  maxHeight: '300px', 
                  overflowY: 'auto', 
                  backgroundColor: '#f5f5f5',
                  padding: '10px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {streamText}
              </div>
            </div>
          )}
        </div>
      </div>


    </div>
    
  );
}

export default DBActions;